import { useMemo, useRef, useState } from 'preact/hooks';
import type { TokenDetail } from '../api.js';
import { quoteSell } from '../../../src/core/curve.js';
import { useStore } from '../store.js';
import { baseFees } from '../launch.js';
import { zec } from '../format.js';
import { toast } from './bits.js';

/**
 * Profit and loss on one coin across this wallet's accounts, and an opt-in
 * picture of it to share. Nothing is sent anywhere: the image is drawn in
 * the browser and the person decides where to post it.
 */
export function PnlCard({ d }: { d: TokenDetail }) {
  const s = useStore();
  const [open, setOpen] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);
  const t = d.token;

  const pnl = useMemo(() => {
    let bought = 0n;
    let sold = 0n;
    let held = 0n;
    for (const a of s.accounts) {
      for (const tr of a.data.trades ?? []) {
        if (tr.token !== t.id) continue;
        if (tr.side === 'buy') bought += BigInt(tr.zat);
        else sold += BigInt(tr.zat);
      }
      held += BigInt(a.data.tokens?.find((x) => x.id === t.id)?.balance ?? '0');
    }
    if (bought === 0n || !s.info) return null;
    let value = 0n;
    if (held > 0n && !t.launchOpen) {
      try {
        const pool = { mode: t.mode, vZat: BigInt(d.pool.vZat), vTok: BigInt(d.pool.vTok), realZat: BigInt(d.pool.realZat), realTok: BigInt(d.pool.realTok), reserveTok: BigInt(d.pool.reserveTok) };
        value = quoteSell(pool, held, baseFees(s.info.genesis)).zatOut;
      } catch {
        value = 0n;
      }
    }
    const gain = sold + value - bought;
    return { bought, sold, value, gain, pct: Number((gain * 10_000n) / bought) / 100 };
  }, [s.accounts, d, s.info]);

  if (!pnl) return null;

  const draw = () => {
    const c = canvas.current;
    if (!c) return;
    const x = c.getContext('2d')!;
    const up = pnl.gain >= 0n;
    x.fillStyle = '#0f1c17';
    x.fillRect(0, 0, 1200, 630);
    x.fillStyle = '#f4b728';
    x.fillRect(0, 0, 1200, 12);
    x.fillStyle = '#e8efe9';
    x.font = '700 64px "Big Shoulders Display", "Arial Narrow", sans-serif';
    x.fillText(`$${t.symbol}`, 72, 150);
    x.fillStyle = up ? '#4cc38a' : '#ef6a5b';
    x.font = '700 180px "Big Shoulders Display", "Arial Narrow", sans-serif';
    x.fillText(`${up ? '+' : ''}${pnl.pct.toFixed(1)}%`, 72, 360);
    x.font = '600 44px Figtree, system-ui, sans-serif';
    x.fillText(`${up ? '+' : '−'}${zec(up ? pnl.gain : -pnl.gain, 4)} ZEC`, 72, 440);
    x.fillStyle = '#8aa196';
    x.font = '400 32px Figtree, system-ui, sans-serif';
    x.fillText(`in ${zec(pnl.bought, 4)} ZEC · out ${zec(pnl.sold + pnl.value, 4)} ZEC`, 72, 500);
    x.fillStyle = '#e8efe9';
    x.font = '700 40px "Big Shoulders Display", "Arial Narrow", sans-serif';
    x.fillText('dazzle', 72, 580);
    x.fillStyle = '#8aa196';
    x.font = '400 28px Figtree, system-ui, sans-serif';
    x.fillText('private memecoins on Zcash', 200, 580);
  };

  const download = () => {
    const c = canvas.current;
    if (!c) return;
    const a = document.createElement('a');
    a.download = `dazzle-${t.symbol}-pnl.png`;
    a.href = c.toDataURL('image/png');
    a.click();
  };

  const tweet = () => {
    const text = `${pnl.gain >= 0n ? '+' : ''}${pnl.pct.toFixed(1)}% on $${t.symbol} on dazzle, private memecoins on Zcash.`;
    window.open(`https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(location.href)}`, '_blank', 'noopener');
    toast('Attach the downloaded picture to your post');
  };

  return (
    <div class="panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>Your result</strong>
        <span class={pnl.gain >= 0n ? 'up' : 'down'}>
          {pnl.gain >= 0n ? '+' : '−'}
          {zec(pnl.gain >= 0n ? pnl.gain : -pnl.gain, 6)} ZEC ({pnl.pct.toFixed(1)}%)
        </span>
      </div>
      <p class="muted" style={{ fontSize: 13, margin: '6px 0 10px' }}>
        Paid {zec(pnl.bought, 6)}, sold for {zec(pnl.sold, 6)}, the rest is worth {zec(pnl.value, 6)} ZEC if sold now.
      </p>
      {!open ? (
        <button
          class="btn ghost block"
          onClick={() => {
            setOpen(true);
            setTimeout(draw, 0);
          }}
        >
          Make a picture to share
        </button>
      ) : (
        <>
          <canvas ref={canvas} width={1200} height={630} class="pnl-canvas" aria-label={`Result on ${t.symbol}: ${pnl.pct.toFixed(1)}%`} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button class="btn ghost" onClick={download}>
              Download
            </button>
            <button class="btn ghost" onClick={tweet}>
              Post on X
            </button>
          </div>
          <p class="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
            Only you decide to share it. The picture shows amounts, never your accounts.
          </p>
        </>
      )}
    </div>
  );
}
